/**
 * Shared visibility gate for PE / IU nudges (interstitial intro, itinerary banner).
 * Visibility = per-search suppression (if `query`) + 31/month cap, separate per kind.
 * Counts exactly one impression per hook mount. SSR-safe: false on server/first render.
 */
import { useEffect, useRef, useState } from 'react'
import { NUDGE_KIND } from '@/constants/flights/premiumEconomy'
import { checkFrequencyCaps, commitFrequencyShow, NudgeKind } from './frequency'

interface UsePeNudgeGateArgs {
    /** BE-driven eligibility for this surface. */
    eligible: boolean
    kind?: NudgeKind
    /** Search identity; when set, a prior interaction on this search suppresses the nudge. Omit on itinerary. */
    query?: string
}

export function usePeNudgeGate({ eligible, kind = NUDGE_KIND.PE, query }: UsePeNudgeGateArgs): boolean {
    const [visible, setVisible] = useState(false)
    const committedRef = useRef(false)

    useEffect(() => {
        if (!eligible) {
            setVisible(false)
            return
        }
        if (committedRef.current) {
            setVisible(true)
            return
        }
        if (!checkFrequencyCaps(kind, query)) return
        committedRef.current = true
        commitFrequencyShow(kind)
        setVisible(true)
    }, [eligible, kind, query])

    return visible
}
